
import {
  Vue, Component, PropSync, Prop, Watch, Inject,
} from 'vue-property-decorator';
import { InputValidationRules } from 'vuetify';
import { PartItem } from '@/features/shipment/types';
import { updatePartsBookItem } from '@/features/shipment/services/parts-book.services';
import { showErrorAlertDialog, showSuccessAlertDialog } from '@/features/common/helpers';
import {
  requiredRules,
  numberRules,
  maxNumberRules,
  hsCodeRules,
  descriptionVagueRules,
  minCharactersRules,
  charactersLimitRules,
} from '@/features/common/helpers/validationRules';
import {
  CommercialInvoiceValidationState,
  rulesIfAttempted as rulesIfAttemptedFn,
} from '@/features/shipment/helpers/commercialInvoiceValidation';
import { quantityUnits } from '@/features/common/raw';
import CountrySelect from '@/features/common/components/CountrySelect.vue';

@Component({
  components: {
    CountrySelect,
  },
})
export default class EditPartsBookDialog extends Vue {
  @Inject({
    from: 'commercialInvoiceValidationState',
    default: () => ({ attempted: true }),
  })
  private commercialInvoiceValidationState!: CommercialInvoiceValidationState;

  @PropSync('show', { required: true }) syncedShow!: boolean;

  @Prop({ required: true }) partsBookItem!: PartItem;

  private isFormValid = false;

  private isUpdating = false;

  private showPidFields = false;

  private formData: PartItem = {
    id: 0,
    quantity: 0,
    unitValue: 0,
    detailedDescription: '',
    countryOfOrigin: '',
    hsCode: '',
    quantityUnits: '',
    nickname: '',
    merchantProductId: '',
    manufacturerProductId: '',
    standardProductId: '',
  };

  private requiredRules = requiredRules;

  private numberRules = numberRules;

  private maxNumberRules = maxNumberRules;

  private hsCodeRules = hsCodeRules;

  private minCharactersRules = minCharactersRules;

  private pidMaxLengthRules = charactersLimitRules(70);

  private partsDescriptionRules: InputValidationRules = [
    ...this.requiredRules,
    ...descriptionVagueRules,
  ];

  private units = quantityUnits;

  private rulesIfAttempted(rules?: InputValidationRules): InputValidationRules {
    return rulesIfAttemptedFn(rules, this.commercialInvoiceValidationState.attempted);
  }

  @Watch('partsBookItem', { immediate: true, deep: true })
  private onPartsBookItemChange() {
    if (this.partsBookItem) {
      this.formData = { ...this.partsBookItem };
    }
  }

  private handleClose() {
    this.resetForm();
    this.syncedShow = false;
  }

  private resetForm() {
    if (this.partsBookItem) {
      this.formData = { ...this.partsBookItem };
    }
    // Reset form validation
    if (this.$refs.editForm) {
      (this.$refs.editForm as any).resetValidation();
    }
  }

  private prepareFormData(): PartItem {
    const cleanHsCode = this.formData.hsCode ? this.formData.hsCode.replace(/\s/g, '') : '';

    const quantity = this.formData.quantity
      ? Math.min(Math.round(parseFloat(String(this.formData.quantity)) * 1e6) / 1e6, 50000) : 0;
    const unitValue = this.formData.unitValue
      ? Math.min(Math.round(parseFloat(String(this.formData.unitValue)) * 1e6) / 1e6, 1000000) : 0;

    return {
      ...this.formData,
      hsCode: cleanHsCode,
      quantity,
      unitValue,
    };
  }

  private async updatePartsBook() {
    this.commercialInvoiceValidationState.attempted = true;
    await this.$nextTick();

    const form = this.$refs.editForm as { validate: () => boolean } | undefined;
    if (form && !form.validate()) return;

    this.isUpdating = true;

    try {
      const preparedData = this.prepareFormData();
      const response = await updatePartsBookItem(preparedData.id as number, preparedData);

      if (response.successful && response.item) {
        this.$emit('updated', response.item);
        showSuccessAlertDialog(this.$t('alertDialog.partsBookUpdated') as string);
        this.syncedShow = false;
      } else if (response.errorMessage) {
        showErrorAlertDialog(response.errorMessage);
      } else {
        showErrorAlertDialog(this.$t('alertDialog.somethingWentWrong') as string);
      }
    } catch (error) {
      showErrorAlertDialog(this.$t('alertDialog.somethingWentWrong') as string);
    }

    this.isUpdating = false;
  }
}
